import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { PageHeader } from '@/components/PageHeader';

type Resp = {
  conversation: { id: string; title: string | null; userId: string; createdAt: string };
  messages: Array<{
    id: string;
    role: 'user' | 'assistant';
    content: string;
    createdAt: string;
  }>;
};

export function AiConversationDetail() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading } = useQuery<Resp>({
    queryKey: ['/admin/api/ai/conversations', id],
    queryFn: () => api.get<Resp>(`/admin/api/ai/conversations/${id}`),
    enabled: !!id,
  });

  if (isLoading || !data) {
    return (
      <div>
        <PageHeader title="AI Conversation" />
        <div className="ad-card p-6 text-text3">Loading…</div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={data.conversation.title ?? 'Conversation'}
        subtitle={`${data.messages.length} messages`}
        actions={
          <Link to="/ai" className="ad-btn-secondary">
            <ArrowLeft size={14} /> Back
          </Link>
        }
      />

      <div className="ad-card p-6 space-y-3">
        {data.messages.map((m) => (
          <div
            key={m.id}
            className={cn(
              'rounded-lg p-3',
              m.role === 'assistant'
                ? 'bg-accent/5 border border-accent/20'
                : 'bg-surface2 border border-border',
            )}
          >
            <div className="flex justify-between items-center mb-1.5">
              <span
                className={cn(
                  'ad-badge',
                  m.role === 'assistant' ? 'ad-badge-accent' : 'ad-badge-neutral',
                )}
              >
                {m.role}
              </span>
              <span className="text-xs text-text3">
                {new Date(m.createdAt).toLocaleString()}
              </span>
            </div>
            <div className="text-sm whitespace-pre-wrap">{m.content}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
